import isEqual from "lodash/isEqual";

import type {
  IApiResponse,
  ArtShortSerializer,
  IResponse,
} from "@litres-libs/types";
import { rtkqApi } from "../../api/rtkqApi";
import { TagsType } from "../../constants";
import type { IUserArtsWithSubscriptionQueryArgs } from "../userArts.types";
import type { RootState } from "../../../store";
import { patchRemoveArtFromList } from "../utils";

export const archiveApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Список книг в архиве
     */
    getArchivedArts: build.query<
      IApiResponse<ArtShortSerializer[]>["payload"],
      IUserArtsWithSubscriptionQueryArgs
    >({
      query: ({ limit, art_groups, after, only_in_subscription_id }) => ({
        url: "/foundation/api/users/me/arts/archive",
        params: {
          limit,
          art_groups,
          after,
          only_in_subscription_id,
        },
      }),
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.MY_BOOKS as const,
                id,
              })),
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST, TagsType.MY_BOOKS],
      transformResponse: (response: IApiResponse<ArtShortSerializer[]>) => {
        return response.payload;
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.after) {
          // при ручной инвалидации кэша арты могут дублироваться, поэтому добавлена фильтрация по id
          const newData = newResponse.data.filter(
            item => !currentCache.data.find(current => current.id === item.id)
          );
          currentCache.data.push(...newData);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
    }),
    /**
     * Убрать книгу в архив
     */
    archiveUserArts: build.mutation<
      IApiResponse<IResponse>,
      { artIds: number[] }
    >({
      query: ({ artIds }) => ({
        url: "/foundation/api/users/me/archive/arts",
        method: "POST",
        body: { art_ids: artIds },
      }),
      invalidatesTags: (_res, _err, { artIds }) => [
        ...artIds.map(id => ({ type: TagsType.MY_BOOKS, id })),
        { type: TagsType.MY_BOOKS, id: "STATS" },
        "Folders",
      ],
      async onQueryStarted({ artIds }, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;
        const entries = rtkqApi.util
          .selectInvalidatedBy(state, [
            ...artIds.map(id => ({ type: TagsType.MY_BOOKS, id })),
          ])
          .filter(data => data.endpointName !== "getArchivedArts");

        const patches = entries.map(entry =>
          patchRemoveArtFromList(artIds, dispatch, entry)
        );
        try {
          await queryFulfilled;
        } catch (e) {
          console.log(e);
          patches.forEach(patch => {
            patch.undo();
          });
        }
      },
    }),
    /**
     * Восстановление из архива
     */
    restoreFromArchive: build.mutation<
      IApiResponse<IResponse>,
      { artId: number }
    >({
      query: ({ artId }) => ({
        url: `/foundation/api/users/me/arts/${artId}/archive`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { artId }) => [
        { type: TagsType.MY_BOOKS, id: artId },
        { type: TagsType.MY_BOOKS, id: "STATS" },
        "Folders",
      ],
      async onQueryStarted({ artId }, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;
        const archiveEntry = rtkqApi.util
          .selectInvalidatedBy(state, [{ type: TagsType.MY_BOOKS, id: artId }])
          .filter(data => data.endpointName === "getArchivedArts");

        // удаление арта из кэша архива
        const patches = archiveEntry.map(entry =>
          patchRemoveArtFromList(artId, dispatch, entry)
        );
        try {
          await queryFulfilled;
        } catch (e) {
          console.log(e);
          patches.forEach(patch => {
            patch.undo();
          });
        }
      },
    }),
  }),
});
